import { useState } from "react";
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  useTranslate,
  SelectArrayInput,
  SelectInput,
  useResourceContext,
  BooleanInput,
  useCreate
} from "react-admin";
import { RichTextInput } from "ra-input-rich-text";

import * as User from "providers/user";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePhotoUploadSubmitCreate } from "hooks/usePhotoUploadSubmitCreate";
import * as yup from "yup";
import { ReferenceManyToManyInput } from "@react-admin/ra-relationships";
import CustomHeaderActions from "./CustomHeaderActions";
import CustomImageInputWrapper from "./CustomImageInputWrapper";
import CustomImageInput from "./CustomImageInput";
import { useNavigate } from "react-router-dom";
import { CustomToolbar } from "./CustomToolbar";

export const ServiceCategoryChoices = [
  { id: "MANICURE", name: "Manicure" },
  { id: "PEDICURE", name: "Pedicure" },
  { id: "OTHER", name: "Other" }
];

export const AddOnsSchedulingPriorities = [
  { id: "BEFORE", name: "Before" },
  { id: "DURING", name: "During" },
  { id: "AFTER", name: "After" }
];

export default function ServiceCreate() {
  const { permissions } = User.useUser();
  const resource = useResourceContext();
  const translate = useTranslate();
  const [create] = useCreate();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isAddOn = resource === "AddOns";
  const imageKeys = [
    "photo_1_url",
    "photo_2_url",
    "photo_3_url",
    "photo_4_url",
    "photo_5_url"
  ];
  const { submit: submitWithUpload } = usePhotoUploadSubmitCreate({
    redirect: `/dashboard/Service`,
    resource: "Service",
    keys: imageKeys,
    setIsLoading: setIsLoading
  });

  const submit = async (data: any) => {
    setIsLoading(true);
    const payload = {
      ...data,
      clean_up_time: data?.clean_up_time ? data.clean_up_time : 0,
      company_id: permissions.companyId
    };

    if (isAddOn) {
      const addOnServicesPayload = data[
        "@@ra-many-to-many/AddOns/ServiceAddOn/Service"
      ]?.map((addOnServiceId: string) => {
        return {
          id: addOnServiceId
        };
      });
      payload.is_addon = true;

      await create(
        "Service",
        {
          data: payload
        },
        {
          onSuccess: async (newServiceData) => {
            if (newServiceData?.id) {
              for (const addOnService of addOnServicesPayload) {
                const addOnPayload = {
                  core_service: { id: newServiceData.id },
                  add_on: { id: addOnService.id }
                };
                await create("ServiceAddOn", {
                  data: addOnPayload
                });
              }
              navigate("/dashboard/AddOns");
            }
            setIsLoading(false);
          },
          onError: () => {
            setIsLoading(false);
          }
        }
      );
    } else {
      const facilitiesPayload = data[
        "@@ra-many-to-many/Service/FacilityService/Facility"
      ]?.map((facilityId: string) => {
        return {
          facility: {
            id: facilityId
          }
        };
      });
      if (facilitiesPayload) {
        facilitiesPayload.facilities = facilitiesPayload;
      }
      submitWithUpload(payload);
    }
  };

  const createValidationSchema = () => {
    const baseSchema = {
      name: yup.string().nullable().required("Name required."),
      price: yup.number().nullable().required("Price required."),
      duration: yup.number().nullable().required("Duration required.")
    };

    if (isAddOn) {
      const addOnSchema = {
        scheduling_priority: yup
          .string()
          .oneOf(
            AddOnsSchedulingPriorities.map((item) => item.id),
            "Invalid scheduling priority"
          )
          .required("Scheduling priority required."),
        "@@ra-many-to-many/AddOns/ServiceAddOn/Service": yup
          .array()
          .of(yup.string())
          .min(1, "At least one service must be selected")
          .required("Selected services are required.")
      };

      return yup
        .object()
        .shape({ ...baseSchema, ...addOnSchema })
        .required();
    }
    const serviceSchema = {
      clean_up_time: yup.number().nullable(),
      special_conditions: yup.string().nullable(),
      description: yup.string().nullable()
    };

    return yup
      .object()
      .shape({ ...baseSchema, ...serviceSchema })
      .required();
  };

  return (
    <Create
      actions={
        <CustomHeaderActions
          title={
            isAddOn
              ? translate("pagenames.add_on")
              : translate("pagenames.service")
          }
        />
      }
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(createValidationSchema())}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <TextInput source="name" label={translate("services.name")} fullWidth />
        <RichTextInput
          source="description"
          label={translate("services.description")}
          fullWidth
          style={{
            backgroundColor: "#fff"
          }}
        />
        <NumberInput
          source="price"
          label={translate("services.price")}
          fullWidth
        />
        <NumberInput
          source="duration"
          label={translate("services.duration")}
          fullWidth
          min={0}
          max={180}
          step={1}
        />
        {isAddOn && (
          <>
            <BooleanInput
              source="choice_is_required"
              label={translate("services.choice_is_required")}
              fullWidth
              defaultValue={false}
            />
            <SelectInput
              source="scheduling_priority"
              label={translate("services.scheduling_priority")}
              fullWidth
              choices={AddOnsSchedulingPriorities}
            />
            <ReferenceManyToManyInput
              source="id"
              reference="Service"
              through="ServiceAddOn"
              using="service_id,add_on_service_id"
              filterChoices={{
                company_id: permissions.companyId,
                is_addon: false
              }}
              perPageChoices={3000}
            >
              <SelectArrayInput
                optionText={(value) => value.name}
                label={translate("facility.services")}
                fullWidth
              />
            </ReferenceManyToManyInput>
          </>
        )}
        {!isAddOn && (
          <>
            <NumberInput
              source="clean_up_time"
              label={translate("services.clean_up_time")}
              fullWidth
            />
            <SelectInput
              source="category"
              label={translate("services.category")}
              fullWidth
              choices={ServiceCategoryChoices}
              defaultValue="OTHER"
            />
            <CustomImageInputWrapper label="Gallery (Recommended dimension 1125x632) ">
              {imageKeys.map((imageKey, index) => (
                <CustomImageInput
                  key={imageKey}
                  source={imageKey}
                  height="140px"
                  badgeNumber={index + 1}
                />
              ))}
            </CustomImageInputWrapper>
            <TextInput
              source="special_conditions"
              label={translate("services.special_conditions")}
              multiline
              fullWidth
            />
          </>
        )}
      </SimpleForm>
    </Create>
  );
}
